import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, Check, Info, AlertTriangle } from "lucide-react";
import { Reveal, useSeo } from "../lib/motion";
import { Breadcrumb, CTASection } from "../components/CTASection";
import { useSettings } from "../context/SettingsContext";

const DEVIS_ENTRETIEN = "/demande-de-devis?univers=climatisation&nature=maintenance";

const PONCTUEL_ROWS = [
  { key: "mono", label: "Mono-split", def: 130 },
  { key: "bi", label: "Bi-split", def: 150 },
  { key: "multi3", label: "Multi-split – 3 unités intérieures", def: 200 },
  { key: "multi4", label: "Multi-split – 4 unités intérieures", def: 240 },
  { key: "multi5", label: "Multi-split – 5 unités intérieures", def: 280 },
  { key: "gainable", label: "Gainable", def: null },
  { key: "cassette", label: "Cassette / installation professionnelle", def: null },
  { key: "six_plus", label: "6 unités intérieures et plus", def: null },
];

const INCLUS = [
  "Contrôle général du fonctionnement",
  "Nettoyage des filtres",
  "Nettoyage des éléments accessibles",
  "Contrôle visuel des unités",
  "Contrôle de l’évacuation des condensats",
  "Vérification des températures de fonctionnement",
  "Vérification des anomalies visibles",
  "Signalement au client des défauts constatés",
];

const ESSENTIEL_DEF = [
  "Entretien annuel de l’installation",
  "Paiement réparti sur 12 mois",
  "Rappel automatique de l’entretien",
  "Suivi de l’installation",
  "Compte-rendu et informations utiles après intervention",
];

const SERENITE_DEF = [
  "Tout ce qui est inclus dans la formule Essentiel",
  "1 intervention de dépannage par période contractuelle de 12 mois",
  "Suivi prioritaire des demandes contractuelles",
  "Historique de l’installation",
  "Rappel automatique de l’entretien",
];

const COMPARATIF = [
  ["Engagement", "Non", "12 mois", "12 mois"],
  ["Entretien", "Oui", "Oui", "Oui"],
  ["Paiement mensuel", "Non", "Oui", "Oui"],
  ["Rappel entretien", "Non", "Oui", "Oui"],
  ["Suivi installation", "Standard", "Oui", "Oui"],
  ["1 dépannage inclus", "Non", "Non", "Oui*"],
  ["Pièces incluses", "Non", "Non", "Non"],
];

const UNITS = [["mono", "Mono-split"], ["bi", "Bi-split"], ["u3", "3 unités"], ["u4", "4 unités"], ["u5", "5 unités"]];

const listFrom = (str, def) => {
  const arr = (str || "").split("\n").map((x) => x.trim()).filter(Boolean);
  return arr.length ? arr : def;
};

function Section({ tint = "white", children, testid }) {
  return (
    <section data-testid={testid} className={tint === "cloud" ? "bg-cloud" : "bg-white"}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">{children}</div>
    </section>
  );
}

function Head({ kicker, title, sub }) {
  return (
    <Reveal className="mb-8 max-w-3xl">
      <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">{kicker}</span>
      <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl text-ink mt-4 leading-[1.05]">{title}</h2>
      {sub && <p className="text-maintext text-lg mt-4 leading-relaxed">{sub}</p>}
    </Reveal>
  );
}

function OfferCard({ testid, title, badge, items, monthly, note, featured }) {
  const valid = monthly.filter((m) => !isNaN(m.v));
  const min = valid.length ? Math.min(...valid.map((m) => m.v)) : null;
  return (
    <div data-testid={testid} className={`flex flex-col h-full bg-white border rounded-md p-7 ${featured ? "border-ice shadow-lg" : "border-line"}`}>
      <span className={`self-start text-xs font-bold uppercase tracking-widest rounded px-2.5 py-1 ${featured ? "bg-ice text-white" : "bg-cloud text-ice border border-line"}`}>{badge}</span>
      <h3 className="font-heading font-black text-2xl text-ink mt-4">{title}</h3>
      <p className="font-heading font-bold text-ice text-xl mt-3" data-testid={`${testid}-prix`}>
        {min !== null ? <>À partir de {min} € HT <span className="text-sm font-semibold text-muted2">/ mois</span></> : <span className="text-muted2">Sur devis</span>}
      </p>
      {valid.length > 0 && (
        <ul className="mt-3 space-y-1">
          {valid.map((m) => <li key={m.k} className="text-xs text-muted2">{m.l} — {m.v} € HT / mois</li>)}
        </ul>
      )}
      <ul className="mt-5 space-y-2.5 flex-1">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed"><Check size={16} className="text-ice mt-0.5 shrink-0" /> {it}</li>
        ))}
      </ul>
      {note && <p className="text-muted2 text-xs leading-relaxed mt-5 border-t border-line pt-4">{note}</p>}
      <Link to={DEVIS_ENTRETIEN} className={`inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 font-bold mt-6 transition-transform hover:scale-[1.02] ${featured ? "bg-ice text-white hover:bg-icehover" : "bg-ink text-white hover:bg-inkhover"}`}>
        Demander cette formule <ArrowUpRight size={17} />
      </Link>
    </div>
  );
}

export default function Entretien() {
  useSeo("Entretien de climatisation", "Entretien ponctuel ou contrat annuel mensualisé pour votre climatisation en Alsace : tarifs HT clairs, sans abonnement ou en contrat Essentiel / Sérénité.");
  const { settings } = useSettings();
  const pr = settings.pricing || {};
  const po = pr.ponctuel || {};

  const monthlyFor = (grp) => UNITS.map(([k, l]) => ({ k, l, v: parseFloat((pr[grp] || {})[k]) }));
  const essentielItems = listFrom(settings.essentiel_includes, ESSENTIEL_DEF);
  const sereniteItems = listFrom(settings.serenite_includes, SERENITE_DEF);

  return (
    <div className="pt-32 bg-white">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16" data-testid="entretien-hero">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Entretien" }]} />
        <Reveal className="mt-8 max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-ice"><CalendarCheck size={15} /> Maintenance</span>
          <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl text-ink mt-5 leading-[0.98]">Entretien de climatisation</h1>
          <p className="text-maintext text-lg mt-6 leading-relaxed">Préservez les performances, la propreté et la fiabilité de votre installation grâce à un entretien régulier.</p>
          <Link to={DEVIS_ENTRETIEN} data-testid="entretien-cta" className="inline-flex items-center gap-2 bg-ice text-white rounded-md px-8 py-4 font-bold mt-8 hover:bg-icehover transition-transform hover:scale-105">
            Réserver un entretien <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>

      {/* TARIFS PONCTUEL */}
      <Section tint="cloud" testid="entretien-ponctuel">
        <Head kicker="Sans abonnement" title="Entretien ponctuel" sub="Un entretien lorsque vous en avez besoin, sans abonnement." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PONCTUEL_ROWS.map((r, i) => {
            const v = po[r.key] !== undefined && po[r.key] !== "" ? po[r.key] : r.def;
            return (
              <Reveal key={r.key} delay={i * 0.04}>
                <div className="bg-white border border-line rounded-md p-6 h-full" data-testid={`price-${r.key}`}>
                  <p className="font-heading font-bold text-ink leading-snug">{r.label}</p>
                  <p className="font-heading font-black text-2xl text-ice mt-3">{v !== null && v !== undefined ? <>{v} € <span className="text-sm font-semibold text-muted2">HT</span></> : <span className="text-muted2 text-lg">Sur devis</span>}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-6 max-w-3xl space-y-2.5">
          <p className="flex items-start gap-2.5 text-muted2 text-sm leading-relaxed"><Info size={15} className="text-ice mt-0.5 shrink-0" /> Tarifs sous réserve d’accessibilité normale des équipements et d’une installation en état d’entretien courant.</p>
          <p className="flex items-start gap-2.5 text-muted2 text-sm leading-relaxed" data-testid="note-deplacement"><Info size={15} className="text-ice mt-0.5 shrink-0" /> Le déplacement n’est pas inclus dans les tarifs de maintenance : le tarif s’ajuste en fonction de la localité du chantier.</p>
        </Reveal>
      </Section>

      {/* CE QUE COMPREND L'ENTRETIEN */}
      <Section testid="entretien-inclus">
        <Head kicker="Prestation" title="Ce que comprend notre entretien" />
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3.5 max-w-3xl">
          {INCLUS.map((it) => (
            <p key={it} className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed"><Check size={17} className="text-ice mt-0.5 shrink-0" /> {it}</p>
          ))}
        </div>
        <Reveal className="mt-8 max-w-3xl">
          <div className="border-l-4 border-amber-500 bg-amber-50 rounded-r-md p-5">
            <p className="flex items-center gap-2 font-heading font-bold text-ink text-sm uppercase tracking-widest"><AlertTriangle size={16} className="text-amber-600" /> Non inclus</p>
            <p className="text-maintext text-[15px] mt-2.5 leading-relaxed">L’entretien n’inclut pas les réparations, pièces détachées, recherche de fuite approfondie ou appoint de fluide frigorigène. Ces prestations font l’objet d’un diagnostic et, si nécessaire, d’un devis complémentaire.</p>
          </div>
        </Reveal>
      </Section>

      {/* CONTRATS MENSUALISÉS */}
      <Section tint="cloud" testid="entretien-contrats">
        <Head kicker="Contrats annuels" title="Vous préférez mensualiser votre entretien ?" sub="Il s’agit d’un contrat annuel d’entretien payé mensuellement. L’intervention d’entretien prévue au contrat est réalisée selon les modalités convenues. Il ne s’agit pas d’un passage chaque mois." />
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
          <Reveal>
            <OfferCard testid="offer-essentiel" title="Essentiel" badge="Contrat annuel" items={essentielItems} monthly={monthlyFor("essentiel")} />
          </Reveal>
          <Reveal delay={0.08}>
            <OfferCard
              testid="offer-serenite"
              title="Sérénité"
              badge="Entretien + dépannage"
              items={sereniteItems}
              monthly={monthlyFor("serenite")}
              featured
              note="Déplacement et diagnostic inclus dans la limite d’une intervention par période de 12 mois. Les pièces détachées, consommables, fluides frigorigènes et travaux de réparation restent facturés séparément après accord du client."
            />
          </Reveal>
        </div>
      </Section>

      {/* COMPARATEUR */}
      <Section testid="entretien-comparatif">
        <Head kicker="Vue d’ensemble" title="Comparer les 3 formules" />
        <Reveal>
          <div className="overflow-x-auto bg-white border border-line rounded-md">
            <table className="w-full min-w-[560px] text-left text-[15px]">
              <thead>
                <tr className="border-b border-line bg-cloud">
                  <th className="px-5 py-4 font-heading font-bold text-ink text-sm uppercase tracking-wider">Formule</th>
                  <th className="px-5 py-4 font-heading font-bold text-ink text-sm uppercase tracking-wider">Ponctuel</th>
                  <th className="px-5 py-4 font-heading font-bold text-ink text-sm uppercase tracking-wider">Essentiel</th>
                  <th className="px-5 py-4 font-heading font-bold text-ice text-sm uppercase tracking-wider">Sérénité</th>
                </tr>
              </thead>
              <tbody>
                {COMPARATIF.map(([label, a, b, c]) => (
                  <tr key={label} className="border-b border-line last:border-0 hover:bg-ice/5 transition-colors">
                    <td className="px-5 py-3.5 font-semibold text-ink">{label}</td>
                    <td className="px-5 py-3.5 text-maintext">{a}</td>
                    <td className="px-5 py-3.5 text-maintext">{b}</td>
                    <td className="px-5 py-3.5 text-maintext">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal className="mt-5 max-w-3xl">
          <p className="text-muted2 text-sm leading-relaxed">* Déplacement + diagnostic inclus pour une intervention par période contractuelle. Pièces, fluides et réparations éventuelles non compris.</p>
        </Reveal>
      </Section>

      <CTASection title="Un entretien à planifier ?" text="Entretien ponctuel ou contrat annuel : décrivez votre installation, nous vous répondons avec la formule adaptée." />
    </div>
  );
}
